/**
 * Messages Context - State management for threaded chat
 * TODO(Mudo): replace mock loadThreads/loadMessages with Supabase RPC
 * TODO(Mudo): subscribe to messages channel for real-time updates
 * TODO(Mudo): persist read receipts per user
 * 
 * Data Models (JSDoc):
 * Thread: { id, participants[], participantNames{}, participantAvatars{}, lastMessage, lastMessageTime, updatedAt, unread }
 * Message: { id, threadId, senderId, receiverId, body, createdAt, read }
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import * as messagesApi from '../../lib/mudoNetwork/messagesApi';
import * as usersApi from '../../lib/mudoNetwork/usersApi';
import { useAuth } from '../../contexts/AuthContext';

const MessagesContext = createContext(null);

export function MessagesProvider({ children, initialThreadId = null }) {
  const { user } = useAuth();  // Use centralized auth context
  const [userId, setUserId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [messagesByThread, setMessagesByThread] = useState({});
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [namesByUserId, setNamesByUserId] = useState({});

  // Local decrypt helper (mirrors messagesApi logic)
  const decrypt = useCallback((encrypted) => {
    try {
      return decodeURIComponent(escape(atob(encrypted)));
    } catch {
      return encrypted;
    }
  }, []);

  // Get current user ID from Supabase auth
  useEffect(() => {
    let mounted = true;

    async function syncUserFromSupabase() {
      try {
        if (!supabase) {
          if (mounted) {
            setUserId(null);
            setLoading(false);
          }
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (mounted) {
          if (user?.id) {
            console.log('MessagesContext: Got user ID:', user.id);
            setUserId(user.id);
          } else {
            console.log('MessagesContext: No Supabase user session');
            setUserId(null);
          }
        }
      } catch (err) {
        console.error('MessagesContext: Error getting user ID:', err);
        if (mounted) setUserId(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    syncUserFromSupabase();

    return () => {
      mounted = false;
    };
  }, []);

  // Sync userId from centralized auth context (replaces auth subscription)
  useEffect(() => {
    setUserId(user?.id || null);
    if (user?.id) {
      console.log('MessagesContext: Synced user ID from AuthContext:', user.id);
    }
  }, [user]);

  // Realtime: subscribe to new messages (INSERT) and updates (optional)
  useEffect(() => {
    if (!userId || !supabase?.channel) return;

    const channel = supabase.channel(`messages_rt_${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          const row = payload.new;
          if (!row) return;
          // Only process rows involving this user
          if (row.sender_id !== userId && row.recipient_id !== userId) return;

          const msg = { ...row, content: decrypt(row.encrypted_content) };
          const otherUserId = row.sender_id === userId ? row.recipient_id : row.sender_id;
          const threadId = [userId, otherUserId].sort().join('_');

          // Ensure other user's profile is available (for avatars/names)
          if (!userProfiles[otherUserId]) {
            try {
              const { data: prof } = await usersApi.getUserProfile(otherUserId);
              if (prof) {
                setUserProfiles((prev) => ({ ...prev, [otherUserId]: prof }));
              }
            } catch {}
          }

          // Upsert message into messagesByThread (avoid duplicates)
          setMessagesByThread((prev) => {
            const existing = prev[threadId] || [];
            if (existing.some((m) => m.id === msg.id)) return prev;
            const next = [...existing, msg].sort((a, b) =>
              new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime()
            );
            return { ...prev, [threadId]: next };
          });

          // Upsert/update thread entry
          setThreads((prev) => {
            const idx = prev.findIndex((t) => t.id === threadId);
            const lastMessage = msg.content;
            const lastTime = msg.created_at;
            const unread = !msg.is_read && msg.recipient_id === userId;

            if (idx >= 0) {
              const t = prev[idx];
              const updated = {
                ...t,
                lastMessage,
                lastMessageTime: lastTime,
                updatedAt: lastTime,
                unread: t.unread || unread,
                messages: undefined, // keep memory light; messagesByThread is source of truth
              };
              const clone = [...prev];
              clone[idx] = updated;
              // sort by updatedAt desc
              clone.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
              return clone;
            }

            const otherProfile = userProfiles[otherUserId] || {};
            const newThread = {
              id: threadId,
              participants: [userId, otherUserId],
              lastMessage,
              lastMessageTime: lastTime,
              updatedAt: lastTime,
              unread,
              participantNames: {
                [userId]: 'You',
                [otherUserId]: otherProfile.display_name || 'User',
              },
              participantAvatars: {
                [userId]: '🙂',
                [otherUserId]: otherProfile.avatar_url || otherProfile.avatar_emoji || '🙂',
              },
            };
            return [newThread, ...prev];
          });
        }
      )
      // Optional: keep read status in sync if other side updates
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        (payload) => {
          const row = payload.new;
          if (!row) return;
          if (row.sender_id !== userId && row.recipient_id !== userId) return;

          const otherUserId = row.sender_id === userId ? row.recipient_id : row.sender_id;
          const threadId = [userId, otherUserId].sort().join('_');
          setMessagesByThread((prev) => ({
            ...prev,
            [threadId]: (prev[threadId] || []).map((m) => (m.id === row.id ? { ...m, is_read: row.is_read, read_at: row.read_at } : m)),
          }));
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [userId, decrypt, userProfiles]);

  // Load threads from actual messages
  const loadThreads = useCallback(async () => {
    if (!userId) {
      console.log('MessagesContext: No userId, skipping loadThreads');
      setLoading(false);
      return;
    }
    
    console.log('MessagesContext: Loading threads for userId:', userId);
    setLoading(true);
    try {
      // Load both inbox and sent messages
      const [inboxResult, sentResult] = await Promise.all([
        messagesApi.getInboxMessages(100),
        messagesApi.getSentMessages(100),
      ]);

      console.log('MessagesContext: Inbox result:', inboxResult);
      console.log('MessagesContext: Sent result:', sentResult);

      const allMessages = [
        ...(inboxResult.data || []),
        ...(sentResult.data || []),
      ];
      
      console.log('MessagesContext: Total messages:', allMessages.length);

      // Group messages into threads
      const threadsMap = new Map();
      const userIds = new Set();

      allMessages.forEach((msg) => {
        const otherUserId = msg.sender_id === userId ? msg.recipient_id : msg.sender_id;
        const threadKey = [userId, otherUserId].sort().join('_');
        
        userIds.add(msg.sender_id);
        userIds.add(msg.recipient_id);

        if (!threadsMap.has(threadKey)) {
          threadsMap.set(threadKey, {
            id: threadKey,
            participants: [userId, otherUserId],
            messages: [],
            lastMessage: msg.content,
            lastMessageTime: msg.created_at,
            updatedAt: msg.created_at,
            unread: !msg.is_read && msg.recipient_id === userId,
          });
        }

        const thread = threadsMap.get(threadKey);
        thread.messages.push(msg);
        
        // Update last message if newer
        if (new Date(msg.created_at) > new Date(thread.updatedAt)) {
          thread.lastMessage = msg.content;
          thread.lastMessageTime = msg.created_at;
          thread.updatedAt = msg.created_at;
        }

        // Mark unread if any message is unread
        if (!msg.is_read && msg.recipient_id === userId) {
          thread.unread = true;
        }
      });

      // Load user profiles
      const allIds = Array.from(userIds).filter(Boolean);
      const { data: profiles } = await usersApi.getUserProfiles(allIds);
      const profilesMap = {};
      (profiles || []).forEach((p) => {
        profilesMap[p.id] = p;
      });
      setUserProfiles(profilesMap);

      // Prefer studio name when available
      let studioOwnerNameByUser = {};
      try {
        if (allIds.length > 0) {
          const { data: studios } = await supabase
            .from('studios')
            .select('owner_user_id,name')
            .in('owner_user_id', allIds);
          (studios || []).forEach((s) => {
            if (s?.owner_user_id) studioOwnerNameByUser[s.owner_user_id] = s.name;
          });
        }
      } catch (e) {
        console.warn('MessagesContext: could not load studio names', e);
      }

      const namesMap = {};
      allIds.forEach((id) => {
        namesMap[id] = studioOwnerNameByUser[id] || profilesMap[id]?.display_name || 'User';
      });
      setNamesByUserId(namesMap);

      // Convert to array and add participant info
      const threadsArray = Array.from(threadsMap.values()).map((thread) => ({
        ...thread,
        participantNames: thread.participants.reduce((acc, id) => {
          acc[id] = namesMap[id] || profilesMap[id]?.display_name || 'User';
          return acc;
        }, {}),
        participantAvatars: thread.participants.reduce((acc, id) => {
          const profile = profilesMap[id];
          acc[id] = profile?.avatar_url || profile?.avatar_emoji || '👤';
          return acc;
        }, {}),
      }));

      console.log('MessagesContext: Threads before sorting:', threadsArray.map(t => ({
        id: t.id,
        participants: t.participants,
        messageCount: t.messages.length,
        lastMessage: t.lastMessage?.substring(0, 30)
      })));

      // Sort by most recent
      threadsArray.sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );

      setThreads(threadsArray);

      // Store messages by thread
      const msgsByThread = {};
      threadsArray.forEach((thread) => {
        msgsByThread[thread.id] = thread.messages.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
      setMessagesByThread(msgsByThread);
      
      console.log('MessagesContext: Loaded threads:', threadsArray.length);

    } catch (err) {
      console.error('MessagesContext: Error loading threads:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load messages for a specific thread
  const loadMessages = useCallback(async (threadId) => {
    // Messages already loaded in loadThreads
    return messagesByThread[threadId] || [];
  }, [messagesByThread]);

  // Send a message (optimistic)
  const sendMessage = useCallback(async (threadId, body) => {
    if (!userId || !body.trim()) return null;

    const thread = threads.find((t) => t.id === threadId);
    if (!thread) return null;

    // Resolve recipient robustly
    let recipientId = Array.isArray(thread.participants)
      ? thread.participants.find((id) => id && id !== userId)
      : null;

    if (!recipientId) {
      const msgs = messagesByThread[threadId] || [];
      const last = msgs[msgs.length - 1];
      if (last) {
        recipientId = (last.sender_id && last.sender_id !== userId) ? last.sender_id : last.recipient_id;
      }
    }

    setSendingMessage(true);

    // Optimistic message
    const optimisticMsg = {
      id: `temp_${Date.now()}`,
      threadId,
      senderId: userId,
      receiverId: recipientId,
      body: body.trim(),
      createdAt: new Date().toISOString(),
      read: false,
    };

    // Add optimistic message
    setMessagesByThread((prev) => ({
      ...prev,
      [threadId]: [...(prev[threadId] || []), optimisticMsg],
    }));

    try {
      if (!recipientId) {
        throw new Error('Could not resolve recipient for this thread');
      }

      // Send via API
      const { data, error } = await messagesApi.sendMessage({
        recipientId,
        content: body.trim(),
        subject: 'Chat message',
      });

      if (error) throw error;

      // Replace optimistic with real
      setMessagesByThread((prev) => ({
        ...prev,
        [threadId]: prev[threadId].map((m) =>
          m.id === optimisticMsg.id ? { ...data, body: data.content } : m
        ),
      }));

      // Update thread last message
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId
            ? { ...t, lastMessage: body.trim(), updatedAt: new Date().toISOString() }
            : t
        )
      );

      return data;
    } catch (err) {
      console.error('Error sending message:', err);
      // Keep optimistic bubble but mark as failed so user sees it
      setMessagesByThread((prev) => ({
        ...prev,
        [threadId]: (prev[threadId] || []).map((m) =>
          m.id === optimisticMsg.id ? { ...m, failed: true, error: String(err?.message || 'Failed to send') } : m
        ),
      }));
      return null;
    } finally {
      setSendingMessage(false);
    }
  }, [userId, threads]);

  // Mark thread as read
  const markThreadRead = useCallback(async (threadId) => {
    if (!userId) return;
    
    // Optimistically update UI
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, unread: false } : t))
    );

    // Update in backend
    const msgs = messagesByThread[threadId] || [];
    const unreadMsgs = msgs.filter((m) => !m.is_read && m.recipient_id === userId);
    
    for (const msg of unreadMsgs) {
      try {
        await messagesApi.markAsRead(msg.id);
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }

    // Update local state
    setMessagesByThread((prev) => ({
      ...prev,
      [threadId]: msgs.map((m) =>
        m.recipient_id === userId ? { ...m, is_read: true } : m
      ),
    }));
  }, [userId, messagesByThread]);

  // Delete thread
  const deleteThread = useCallback(async (threadId) => {
    if (!userId || !threadId) return { success: false, error: 'Missing user or thread ID' };

    try {
      // Call backend API to delete all messages in the thread
      const { error } = await messagesApi.deleteThread(threadId);
      if (error) {
        throw error;
      }
      
      // Remove thread from state
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      
      // Remove messages from state
      setMessagesByThread((prev) => {
        const { [threadId]: _, ...rest } = prev;
        return rest;
      });
      
      // Clear active thread if it was deleted
      if (activeThreadId === threadId) {
        setActiveThreadId(null);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting thread:', error);
      return { success: false, error };
    }
  }, [userId, activeThreadId]);  // Search threads
  const searchThreads = useCallback((query) => {
    if (!query.trim()) return threads;

    const q = query.toLowerCase();
    return threads.filter((thread) => {
      const otherUserId = thread.participants.find((id) => id !== userId);
      const otherUserName = thread.participantNames[otherUserId]?.toLowerCase() || '';
      return (
        otherUserName.includes(q) ||
        thread.lastMessage.toLowerCase().includes(q)
      );
    });
  }, [threads, userId]);

  // Load threads on mount and when userId changes
  useEffect(() => {
    if (userId) {
      console.log('MessagesContext: userId changed, calling loadThreads');
      loadThreads();
    }
  }, [userId, loadThreads]);

  // Deep-link: prefer opening a specific thread if provided
  useEffect(() => {
    if (!initialThreadId) return;
    setActiveThreadId(initialThreadId);
  }, [initialThreadId]);

  // Mark active thread as read when opened
  useEffect(() => {
    if (activeThreadId) {
      const thread = threads.find((t) => t.id === activeThreadId);
      if (thread?.unread) {
        markThreadRead(activeThreadId);
      }
    }
  }, [activeThreadId, threads, markThreadRead]);

  const value = {
    threads,
    messagesByThread,
    activeThreadId,
    setActiveThreadId,
    loading,
    sendingMessage,
    userProfiles,
    namesByUserId,
    userId,
    loadThreads,
    loadMessages,
    sendMessage,
    markThreadRead,
    deleteThread,
    searchThreads,
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within MessagesProvider');
  }
  return context;
}
